import { Dispatch, SetStateAction, memo } from "react";
import RadioElement from "../../../../../../Elements/RadioElement/RadioElement";
import useCreditCard from "../../../../hooks/useCreditCard";
import CreditCardDetails from "../CreditCardDetails";
import { useContent } from "../../../../../../../contexts/contentContext/contentContext";
import Image from "../../../../../../Elements/Image/Image";
import { PaymentTypeE } from "../../../../models/enums";
import { ButtonAccessability } from "../../../../../../Elements/Button/Button";
import useCurrBillingCyclePaymentMethod from "../../../../hooks/useCurrBillingCyclePaymentMethod";

import styles from "./styles.module.scss";

interface PaymentTypeListI {
  setShowPaymentTypeList: Dispatch<SetStateAction<boolean>>;

  // If current method is "credit card", we can't change it
  // to PayPal and vice versa.
  showPayPal?: boolean;

  // If you pass onChange prop, all th existing logic
  // of selecting element will be ignored.
  onChange?: (paymentType: PaymentTypeE | null, creditCardId: number) => void;
}

/**
 * Renders a list of payment types for the payment method section.
 *
 * @component
 * @param {PaymentTypeListI} props - The component props.
 * @returns {JSX.Element | null} The rendered component.
 */
const PaymentTypeList = memo((props: PaymentTypeListI) => {
  const { setShowPaymentTypeList, onChange, showPayPal = true } = props;
  const {
    creditCards,
    selectedCreditCardId,
    setSelectedCreditCardId,
    paymentType,
    setPaymentType,
  } = useCreditCard();
  const { getTextByKey } = useContent();
  const currPaymentMethod = useCurrBillingCyclePaymentMethod();

  const setPaymentMethod = async (
    paymentType: PaymentTypeE | null,
    creditCardId: number = 0
  ) => {
    if (onChange) {
      await onChange(paymentType, creditCardId);
      setSelectedCreditCardId(creditCardId);
    } else {
      setShowPaymentTypeList(false);
      if (paymentType) {
        setPaymentType(paymentType);
      } else {
        setPaymentType(PaymentTypeE.CREDIT_CARD);
        setSelectedCreditCardId(creditCardId);
      }

      if (paymentType === PaymentTypeE.NEW_CREDIT_CARD) {
        setSelectedCreditCardId(0);
      }
    }
  };

  return creditCards?.length ? (
    <div className={styles.creditCardsList}>
      {creditCards.map(({ id }) => {
        return (
          <ButtonAccessability
            key={id}
            onClick={() => setPaymentMethod(null, id)}
            buttonClassName={styles.creditCardLineContainer}
          >
            <RadioElement
              specificClass="firstRadio"
              state={selectedCreditCardId}
              setState={() => setPaymentMethod(null, id)}
              fieldToCheck={id}
              textRadioObject={
                <CreditCardDetails
                  paymentType={PaymentTypeE.CREDIT_CARD}
                  creditCardId={id}
                />
              }
              tooltipRadio={""}
            />
          </ButtonAccessability>
        );
      })}
      {showPayPal && !currPaymentMethod?.includes("pelecard") ? (
        <ButtonAccessability
          onClick={() => setPaymentMethod(PaymentTypeE.PAYPAL)}
          buttonClassName={styles.creditCardLineContainer}
        >
          <RadioElement
            specificClass="firstRadio"
            state={paymentType}
            setState={setPaymentMethod}
            fieldToCheck={PaymentTypeE.PAYPAL}
            textRadioObject={
              <CreditCardDetails paymentType={PaymentTypeE.PAYPAL} />
            }
            tooltipRadio={""}
          />
        </ButtonAccessability>
      ) : null}
      <ButtonAccessability
        onClick={() => setPaymentMethod(PaymentTypeE.NEW_CREDIT_CARD)}
        buttonClassName={styles.creditCardLineContainer}
      >
        <RadioElement
          specificClass="firstRadio"
          state={paymentType}
          setState={setPaymentMethod}
          fieldToCheck={PaymentTypeE.NEW_CREDIT_CARD}
          textRadioObject={
            <div className={styles.creditCardLine}>
              <Image name="purchases/credit-card_icon" alt="credit card" title="credit-card"/>
              <div className={styles.newCreditCard}>
                {getTextByKey("add-new-credit-card")}
              </div>
            </div>
          }
          tooltipRadio={""}
        />
      </ButtonAccessability>
    </div>
  ) : null;
});

export default PaymentTypeList;
